import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "./AuthProvider";
import { useConnections } from "./ConnectionProvider";
import { UpgradeModal, useUpgradeModal } from "@/components/UpgradeModal";
import { isFreePlan } from "@/utils/subscription";

const UpgradeModalContext = createContext({
  triggerUpgradeModal: () => {},
});

export const useUpgradeModalContext = () => {
  const context = useContext(UpgradeModalContext);
  if (!context) {
    throw new Error("useUpgradeModalContext must be used within UpgradeModalProvider");
  }
  return context;
};

export const UpgradeModalProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { connections } = useConnections();

  const [modalOpen, setModalOpen] = useState(false);
  const [triggerPlatform, setTriggerPlatform] = useState(null);

  // Track previous connection states to detect new connections
  const prevConnections = useRef(null);
  const isInitialized = useRef(false);

  // Hook for localStorage/24h logic
  const { shouldShowModal, markRemindLater, markDismissed: _markDismissed } = useUpgradeModal(user?.id);

  // Check if user is on free plan
  const userIsOnFreePlan = isFreePlan(user?.currentPlan?.name);

  // Handle successful integration
  const handleIntegrationSuccess = useCallback(
    (platform) => {
      if (userIsOnFreePlan && shouldShowModal) {
        setTriggerPlatform(platform);
        setModalOpen(true);
      }
    },
    [userIsOnFreePlan, shouldShowModal]
  );

  // Manually trigger upgrade modal (for external use)
  const triggerUpgradeModal = useCallback(
    (platform = "whatsapp") => {
      if (userIsOnFreePlan && shouldShowModal) {
        setTriggerPlatform(platform);
        setModalOpen(true);
      }
    },
    [userIsOnFreePlan, shouldShowModal]
  );

  // Detect connection changes
  useEffect(() => {
    // Skip first render - wait for initial state to stabilize
    if (!isInitialized.current) {
      if (connections.whatsapp !== undefined || connections.instagram !== undefined) {
        prevConnections.current = { ...connections };
        isInitialized.current = true;
      }
      return;
    }

    const prev = prevConnections.current;
    if (!prev) {
      prevConnections.current = { ...connections };
      return;
    }

    // WhatsApp: was disconnected and now connected
    if (!prev.whatsapp && connections.whatsapp) {
      handleIntegrationSuccess("whatsapp");
    }

    // Instagram: was disconnected and now connected
    if (!prev.instagram && connections.instagram) {
      handleIntegrationSuccess("instagram");
    }

    // Update previous state
    prevConnections.current = { ...connections };
  }, [connections, handleIntegrationSuccess]);

  // Handle upgrade button click
  const handleUpgrade = () => {
    setModalOpen(false);
    navigate("/planos", { state: { highlightPlan: "pro", source: "upgrade_modal" } });
  };

  // Handle remind later
  const handleRemindLater = () => {
    if (triggerPlatform) {
      markRemindLater(triggerPlatform);
    }
    setModalOpen(false);
    setTriggerPlatform(null);
  };

  const value = {
    triggerUpgradeModal,
  };

  return (
    <UpgradeModalContext.Provider value={value}>
      {children}
      <UpgradeModal
        open={modalOpen}
        platform={triggerPlatform}
        onUpgrade={handleUpgrade}
        onRemindLater={handleRemindLater}
      />
    </UpgradeModalContext.Provider>
  );
};

export default UpgradeModalProvider;
